import { differenceInDays, format, addMonths, isSameMonth } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import type { FC, RefObject, Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { DateRangePicker, type Range } from "react-date-range";
import { useTranslation } from "react-i18next";

import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";

import { useSearch, DEFAULT_PARAMS } from "../../hooks/useSearch";
import { dateToSearchFormat } from "./utils";
import IconChevron from "../../../assets/icons/IconChevron";

import { cn } from "../../../assets/Utils/generic";
import { ScrollLock } from "./Search";
import { SearchParameters } from "../../../assets/Services/wsSearch";
import { getQueryString } from "../../../assets/Utils/search";
import { useLocation } from "react-router-dom";

type DatesModalProps = {
  isCalendarOpen: boolean;
  calendarRef?: RefObject<HTMLDivElement>;
  mode: "mobile" | "desktop";
  shouldOpenOver?: boolean;
  setIsCalendarOpen: Dispatch<SetStateAction<boolean>>;
  calendarLocale: Locale | undefined;
  range: Range;
  setRange: Dispatch<SetStateAction<Range>>;
  setIsLocationOpen: Dispatch<SetStateAction<boolean>>;
  setIsGuestsOpen: Dispatch<SetStateAction<boolean>>;
  parent: "home" | "nav" | "detail";
  searchParameters: SearchParameters;
  setParameters: (params: SearchParameters) => void;
};

const DatesModal: FC<DatesModalProps> = ({
  isCalendarOpen,
  calendarRef,
  mode,
  shouldOpenOver,
  setIsCalendarOpen,
  calendarLocale,
  range,
  setRange,
  setIsLocationOpen,
  setIsGuestsOpen,
  parent,
  searchParameters,
  setParameters,
}) => {
  const { t } = useTranslation();

  const nights = differenceInDays(
    range.endDate ?? new Date(),
    range.startDate ?? new Date(),
  );

  const { pathname } = useLocation();

  const [shownDate, setShownDate] = useState(new Date());

  useEffect(() => {
    const targetElement = document.querySelector(".rdrStartEdge");
    if (targetElement && isCalendarOpen && mode === "mobile") {
      targetElement.scrollIntoView({ block: "center" });
    }
  }, [isCalendarOpen, mode]);

  return (
    <AnimatePresence>
      {isCalendarOpen && (
        <>
          <ScrollLock />
          <motion.div
            ref={calendarRef} // Add ref here
            className={`flex flex-col ${
              mode === "desktop" ? " rounded-3xl" : ""
            }`}
            style={
              mode === "desktop"
                ? {
                    position: "absolute",
                    right:
                      parent === "home"
                        ? "auto"
                        : parent === "nav"
                        ? "-137%"
                        : 0,
                    translateX: parent === "home" ? "-30%" : "0",
                    ...(shouldOpenOver
                      ? { bottom: parent !== "nav" ? "115%" : "140%" }
                      : { top: parent !== "nav" ? "115%" : "140%" }),
                    zIndex: 100,
                    overflow: "hidden",
                    border: "1px solid lightgray",
                    boxShadow: "4px 4px 10px 0px rgba(0, 0, 0, 0.05)",
                    background: "white",
                    minWidth: parent !== "nav" ? "940px" : "auto",
                    // top: parent !== "home" ? "125%" : "115%",
                  }
                : {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    overflow: "hidden",
                    border: "1px solid lightgray",
                    boxShadow: "4px 4px 10px 0px rgba(0, 0, 0, 0.05)",
                    background: "white",
                    height: "100dvh",
                  }
            }
            initial={
              mode === "desktop" ? { opacity: 0 } : { y: 100, opacity: 0 }
            }
            animate={mode === "desktop" ? { opacity: 1 } : { y: 0, opacity: 1 }}
            exit={
              mode === "desktop"
                ? { y: -20, opacity: 0 }
                : { y: 100, opacity: 0 }
            }
            transition={{ duration: 0.2 }}
          >
            {mode === "mobile" && (
              <div className="relative flex justify-end px-3 items-center box-border border-bottom h-12">
                <h3 className="font-medium text-sm text-center mb-0 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                  {t("ds.choose_dates")}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsCalendarOpen(false)}
                  className="bg-transparent border-none touch-hitbox relative"
                >
                  <IconNew icon="close" className="size-5" />
                </button>
              </div>
            )}
            <div className="h-[calc(100dvh-3rem-2.5rem-5rem)] lg:h-auto overflow-y-auto lg:overflow-y-hidden border-bottom lg:border-0 relative">
              {mode === "desktop" && (
                <div className="absolute top-0 left-0 w-full h-[5.375rem] flex items-center justify-between px-5">
                  {!isSameMonth(shownDate, new Date()) && (
                    <button
                      type="button"
                      className="bg-transparent rounded-full size-7 flex items-center justify-center border border-[#E8E8E8]"
                      onClick={() => setShownDate(addMonths(shownDate, -1))}
                    >
                      <IconChevron className="size-2.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="bg-transparent rounded-full size-7 flex items-center justify-center ml-auto border border-[#E8E8E8]"
                    onClick={() => setShownDate(addMonths(shownDate, 1))}
                  >
                    <IconChevron className="size-2.5 rotate-180" />
                  </button>
                </div>
              )}
              <DateRangePicker
                key={shownDate.getTime()}
                showDateDisplay={false}
                rangeColors={["#9AFEE3"]}
                color="#9AFEE3"
                preventSnapRefocus
                locale={calendarLocale}
                minDate={new Date()}
                className={cn("rounded w-full", parent === "nav" && "narrow")}
                onChange={(item) => {
                  setRange(item.selection);

                  setParameters({
                    ...searchParameters,
                    arrivalDate: item.selection.startDate
                      ? dateToSearchFormat(item.selection.startDate)
                      : undefined,
                    departureDate: item.selection.endDate
                      ? dateToSearchFormat(item.selection.endDate)
                      : undefined,
                  });
                  if (
                    item.selection.startDate !== item.selection.endDate &&
                    mode === "desktop" &&
                    parent !== "detail"
                  ) {
                    setIsCalendarOpen(false);

                    if (
                      !searchParameters.destinationLabel &&
                      !searchParameters.tag
                    ) {
                      setIsLocationOpen(true);
                      return;
                    }

                    if (
                      searchParameters.adults === DEFAULT_PARAMS.adults &&
                      searchParameters.childrenAge.length === 0
                    ) {
                      if (parent !== "nav") {
                        setIsGuestsOpen(true);
                      }
                    }
                  }
                }}
                months={mode === "desktop" ? 2 : 12}
                ranges={[range]}
                direction={mode === "desktop" ? "horizontal" : "vertical"}
                staticRanges={[]}
                inputRanges={[]}
                shownDate={shownDate} //{mode === "desktop" ? shownDate : undefined}
              />
              {mode === "mobile" && (
                <div className="w-full p-3 fixed left-0 bottom-0 flex flex-col items-center justify-center gap-4">
                  <>
                    {(range.startDate || range.endDate) && (
                      <p className="flex items-center gap-1 font-medium text-xs">
                        <span>
                          {range.startDate
                            ? format(range.startDate, "iii dd MMM", {
                                locale: calendarLocale,
                              })
                            : "-"}
                        </span>
                        <span>-</span>
                        <span>
                          {range.endDate
                            ? format(range.endDate, "iii dd MMM", {
                                locale: calendarLocale,
                              })
                            : "-"}
                        </span>
                      </p>
                    )}
                  </>
                  <Button
                    onClick={() => {
                      if (parent === "detail") {
                        const queryString = getQueryString(searchParameters);
                        window.location.href = `${String(
                          process.env.REACT_APP_FRONTEND_URI,
                        )}${pathname}?${queryString}`;
                        return;
                      }
                      setIsCalendarOpen(false);

                      if (
                        !searchParameters.destinationLabel &&
                        !searchParameters.tag
                      ) {
                        setIsLocationOpen(true);
                        return;
                      }

                      if (
                        searchParameters.adults === DEFAULT_PARAMS.adults &&
                        searchParameters.childrenAge.length === 0
                      ) {
                        if (parent !== "nav") {
                          setIsGuestsOpen(true);
                        }
                      }
                    }}
                    className="w-full"
                    disabled={nights < 1}
                    text={
                      parent === "detail"
                        ? "Cerca"
                        : nights > 0
                        ? `${t("ds.next")} ‧ ${nights} ${t("ds.night", {
                            count: nights,
                          }).toLowerCase()}`
                        : t("ds.next")
                    }
                  />
                </div>
              )}
              {parent === "detail" && mode === "desktop" ? (
                <div
                  className="w-full flex justify-end p-4 "
                  style={{
                    boxShadow: "0px 2px 12px rgba(12, 14, 28, 0.08)",
                  }}
                >
                  <Button
                    size="sm"
                    className="h-12"
                    textClass="text-sm font-medium"
                    text={t("ds.search")}
                    onClick={() => {
                      const queryString = getQueryString(searchParameters);
                      window.location.href = `${String(
                        process.env.REACT_APP_FRONTEND_URI,
                      )}${pathname}?${queryString}`;
                    }}
                    // disabled={!availability || !availability.availabilityInfo.available}
                  />
                </div>
              ) : null}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DatesModal;
